/* eslint-disable react/display-name */
import React, { useState, useEffect, useRef } from 'react';
import { Cropper } from 'react-cropper';
import { Button, PopupMenu } from 'quantux-ui';
import { StyleSheet, css } from 'aphrodite/no-important';
import { gray, blue, red } from '../../theme';

const CropperContainer = React.forwardRef((props, ref) => {
  const [showThumbnails, setShowThumbnails] = useState(false);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteMode, setDeleteMode] = useState('');
  const [enableZoom, setEnableZoom] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [cropperHeight, setCropperHeight] = useState(0);

  // ---------- Refs ----------
  const viewMenuRef = useRef();
  const cropperParentRef = useRef();
  const zoomRef = useRef();
  const shouldUpdateRef = useRef(true);

  // ----------Effects ----------
  useEffect(() => {
    setEnableZoom(props.enableZoom);
  }, [props.enableZoom]);

  useEffect(() => {
    console.log('Cropper height effect');
    if (shouldUpdateRef.current && cropperParentRef.current) {
      shouldUpdateRef.current = false;
      console.log(`Setting cropper height = ${cropperParentRef.current.getBoundingClientRect().height - 40}`);
      setCropperHeight(cropperParentRef.current.getBoundingClientRect().height - 40);
    }
  });

  // ---------- Styles ----------
  let camHeight = 8000;
  let camWidth = 0;
  let diff = 40;

  if (!props.overrideDimensions) {
    while (camHeight > window.innerHeight - 200) {
      camWidth = Math.min(window.innerWidth - diff);
      camHeight = camWidth / 0.70707071;
      diff = diff + 20;
    }
  } else {
    while (camHeight > props.overrideHeight - 95) {
      camWidth = Math.min(window.innerWidth - diff);
      camHeight = camWidth / 0.70707071;
      diff = diff + 20;
    }
  }

  const styles = StyleSheet.create({
    cropperContainer: {
      height: '100%',
      maxHeight: '100%',
      width: '100%',
      position: 'relative'
    },
    content: {
      display: 'flex',
      // height: props.editMode ? '100%' : 'calc(100% - 100px)'
      // height: props.editMode ? 'calc(100% - 100px)' : '100%',
      height: '100%',
      width: '100%'
    },
    cropper: {
      display: 'flex',
      flexFlow: 'column',
      alignItems: 'center',
      // justifyContent: 'center',
      height: '100%',
      // flexGrow: '1',
      width: '100%',
      background: '#004F7D'
    },
    thumbnailDrawer: {
      top: props.showHeader ? '80px' : '40px',
      // height: props.editMode ? 'calc(100vh - 185px)' : 'calc(100% - 80px)',
      // height: props.editMode ? 'calc(100% - 181px)' : (props.showHeader ? 'calc(100% - 80px)' : 'calc(100% - 40px)'), // eslint-disable-line
      height: 'calc(100% - 40px)',
      width: '101px',
      position: 'absolute',
      // paddingTop: '80px',
      display: showThumbnails ? 'block' : 'none',
      zIndex: '1',
      background: gray[600],
      boxShadow: '4px 0px 4px rgba(0, 0, 0, 0.5)'
    },
    thumbnailDrawerContainer: {
      display: 'flex',
      flexFlow: 'column',
      padding: '0 8px',
      position: 'relative',
      height: '100%',
      overflowY: 'auto'
    },
    thumbnailImage: {
      height: 'auto',
      width: '100%'
    },
    header: {
      background: blue[800],
      width: '100%',
      height: props.showHeader ? '80px' : '40px'
    },
    cropperParent: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // height: 'calc(100% - 60px)',
      height: 'calc(100% - 72px)',
      width: '100%'
    },
    editModeParent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    title: {
      height: '40px',
      display: 'flex',
      padding: '0 8px',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderBottom: `1px solid ${gray[900]}`
    },
    toolbar: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 8px'
    },
    toolbarIcon: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    toolbarNav: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    pageSelector: {
      display: 'flex',
      height: '30px',
      width: '120px',
      border: `1px solid ${gray[800]}`,
      borderRadius: '5px',
      marginLeft: '16px'
    },
    pageSelectorBtn: {
      fontSize: '12px',
      width: '30px',
      height: '30px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: gray[700]
    },
    pageSelectorPageNumberDisplay: {
      width: '80x',
      background: 'black',
      color: gray[300],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexGrow: '1',
      height: '30px'
    },
    closeIcon: {
      fontSize: '24px',
      color: '#96C8FA',
      ':hover': {
        color: red[500]
      }
    },
    footer: {
      height: '100px',
      width: '100vw',
      background: gray[800],
      display: props.editMode ? '' : 'none'
    },
    footerContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '100%',
      padding: '0 16px'
    },
    ellipsisMenuItem: {
      display: 'flex',
      alignItems: 'center',
      height: '40px',
      width: '100%',
      background: '#103A60',
      color: '#96C8FF',
      paddingLeft: '0',
      cursor: 'pointer',
      ':not(.disabled):hover': {
        background: '#0066CC'
      }
    },
    isSelected: {
      background: '#0066CC'
    },
    ellipsisMenuItemIcon: {
      minHeight: '40px',
      minWidth: '40px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      justifyContent: 'center'
    },
    miniBtn: {
      background: '#96C8FA',
      width: '24px',
      height: '24px',
      color: '#184878',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      borderRadius: '4px'
    },
    disabledMiniBtn: {
      background: '#96C8FA',
      opacity: '0.5',
      width: '24px',
      height: '24px',
      color: '#184878',
      fontSize: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      borderRadius: '4px'
    },
    docImage: {
      width: '100%',
      height: 'auto',
      maxHeight: '100%'
      // maxHeight: 'calc(100% - 80px)'
    },
    docImageZoomed: {
      width: 'auto',
      height: 'auto'
    }
  });

  let imageDisplay = null;
  if (enableZoom) {
    imageDisplay = (
      <div
        style={{
          // maxHeight: `${props.cropperStyles.maxHeight - 40}px`,
          // maxWidth: `${props.cropperStyles.maxWidth}px`,
          // maxWidth: '825px',
          // maxHeight: '924px',
          // overflow: 'auto'
          // padding: '16px'
          maxWidth: '90%',
          height: 'calc(100% - 12px)',
          overflow: 'auto'
        }}
      >
        <img
          src={props.croppedImages[props.currentIndex]}
          ref={zoomRef}
          onDoubleClick={() => {
            setEnableZoom(false);
            // props.handleOnZoom();
          }}
          className={css(styles.docImage, isZoomed && styles.docImageZoomed)}
          style={{
            // height: !props.overrideDimensions ? `${camHeight}px` : 'auto',
            // width: !props.overrideDimensions ? 'auto' : `${props.overrideWidth}px`
            ...props.cropperStyles
          }}
        />
      </div>
    );
  } else {
    imageDisplay = (
      <div
        style={{
          // maxHeight: `${props.cropperStyles.maxHeight - 40}px`,
          // maxWidth: `${props.cropperStyles.maxWidth}px`,
          // maxWidth: '825px',
          // maxHeight: '924px',
          // overflow: 'auto'
          // padding: '16px'
          // --- last
          maxWidth: '90%',
          height: 'calc(100% - 12px)',
          overflow: 'auto'
        }}
      >
        <img
          src={props.croppedImages[props.currentIndex]}
          onDoubleClick={() => {
            setEnableZoom(true);
            // props.handleOnZoom();
          }}
          className={css(styles.docImage, isZoomed && styles.docImageZoomed)}
          style={{
            // height: !props.overrideDimensions ? `${camHeight}px` : 'auto',
            // width: !props.overrideDimensions ? 'auto' : `${props.overrideWidth}px`
            ...props.cropperStyles
            // height: 'auto',
            // width: `${camWidth}px`
          }}
        />
      </div>
    );
  }

  return (
    <div className={css(styles.cropperContainer)}>
      {/* Thumbnail Drawer */}
      <div className={css(styles.thumbnailDrawer)}>
        <div className={css(styles.thumbnailDrawerContainer)}>
          {props.croppedImages.map((el, idx) => {
            return (
              <div
                className='mb-2 '
                style={{ border: idx === props.currentIndex ? '2px solid #00A0FF' : '2px solid #306098' }}
                key={`${idx}_${props.keySuffix}`}
              >
                <img
                  className={`${css(styles.thumbnailImage)}`}
                  src={el}
                  onClick={() => { props.loadImage(idx); }}
                />
                <span
                  style={{ color: '#96C8FA', position: 'relative', top: '-4px', left: '13px' }}
                >
                  {`Page ${idx + 1}`}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className={css(styles.content)}>
        {/* Cropper component */}
        <div className={css(styles.cropper)}>
          {/* Header */}
          <div className={css(styles.header)}>
            {/* Title */}
            <div
              className={css(styles.title)}
              style={{ display: props.showHeader ? '' : 'none' }}
            >
              <div style={{ color: '#96C8FA' }}>{props.title}</div>
              <div className='fx fx__hv-center'>
                <div className='mr-4'>
                  <div
                    ref={viewMenuRef}
                    style={{ color: gray[200], cursor: 'pointer' }}
                    onClick={() => setShowViewMenu(true)}
                  >
                    <i className={`pr-2 ${props.editMode ? 'fa fa-pencil' : 'fa fa-eye'}`} />
                    {props.editMode ? 'Edit' : 'View'}
                    <i className='pl-2 fa fa-caret-down' />
                  </div>
                  <PopupMenu
                    top={30}
                    right={50}
                    position="absolute"
                    visible={showViewMenu}
                    parent={viewMenuRef}
                    onOutsideClick={() => { setShowViewMenu(false); }}
                  >
                    <div style={{ background: '#083050', width: '134px' }}>
                      {/* View Menu Item */}
                      <div
                        className={css(styles.ellipsisMenuItem, !props.editMode && styles.isSelected)}
                        onClick={() => {
                          setShowViewMenu(false);
                          props.onModeChange('view');
                        }}
                      >
                        <div className={css(styles.ellipsisMenuItemIcon)}>
                          <i className='fa fa-eye' />
                        </div>
                        View
                      </div>
                      {/* Edit Menu Item */}
                      <div
                        className={css(styles.ellipsisMenuItem, props.editMode && styles.isSelected)}
                        onClick={() => {
                          setShowViewMenu(false);
                          props.onModeChange('edit');
                        }}
                      >
                        <div className={css(styles.ellipsisMenuItemIcon)}>
                          <i className='fa fa-pencil' />
                        </div>
                        Edit
                      </div>
                      <div
                        className={`${css(styles.ellipsisMenuItem)} disabled`}
                        style={{ color: '#96c8ff80' }}
                      >
                        <div
                          className={`${css(styles.ellipsisMenuItemIcon)} disabled`}
                          style={{ color: '#96c8ff80' }}
                        >
                          <i className='fa fa-info-circle' />
                        </div>
                        Info
                      </div>
                    </div>
                  </PopupMenu>
                </div>
                <i
                  className={`fa fa-times-circle ${css(styles.closeIcon)}`}
                  onClick={() => {
                    props.onClose(deleteMode);
                  }}
                />
              </div>
            </div>
            {/* Toolbar / Show Delete Warning */}
            {showDeleteWarning
              ? <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  width: '100%',
                  paddingTop: '4px',
                  paddingRight: '4px'
                }}
              >
                <Button
                  btnType='primary'
                  className='mr-2'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '80px',
                    minHeight: '32px',
                    maxHeight: '32px',
                    fontSize: '14px'
                  }}
                  isOutlineBtn
                  onClick={() => setShowDeleteWarning(false)}
                >
                  Cancel
                </Button>
                <Button
                  btnType='warning'
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '80px',
                    minHeight: '32px',
                    maxHeight: '32px',
                    fontSize: '14px'
                  }}
                  onClick={() => {
                    if (props.images.length === 1) {
                      props.onDelete(props.currentIndex);
                      props.onClose('delete');
                    } else {
                      setDeleteMode('update');
                      props.onDelete(props.currentIndex);
                      setShowDeleteWarning(false);
                    }
                  }}
                >
                  Delete
                </Button>
              </div>
              : <div className={css(styles.toolbar)}>
                {/* Left aligned icons */}
                <div className={css(styles.toolbarNav)}>
                  {/* Drawer Icon */}
                  <div
                    className={css(styles.toolbarIcon)}
                    onClick={() => { setShowThumbnails(!showThumbnails); }}
                  >
                    <svg width="21" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M19.523 0H1.477C.66 0 0 .839 0 1.875v16.25C0 19.162.66 20 1.477 20h18.046C20.34 20 21 19.162 21 18.125V1.875C21 .839 20.34 0 19.523 0ZM8 18H2V2h6v16Zm2 0h9V2c0 .057-.007.095-.014.095h-8.972C10.006 2.095 10 2.047 10 2v16Z" fill="#96C8FF" /></svg>
                  </div>
                  <div className={css(styles.pageSelector)}>
                    <div
                      className={css(styles.pageSelectorBtn)}
                      style={{
                        fontSize: '18px',
                        borderTopLeftRadius: '5px',
                        borderBottomLeftRadius: '5px',
                        color: '#5080B0'
                        // color: red[500]
                      }}
                      onClick={() => { props.loadImage(Math.max(0, props.currentIndex - 1)); }}
                    >
                      <i className='fa fa-minus-circle' />
                    </div>
                    <div
                      className={css(styles.pageSelectorPageNumberDisplay)}>
                      {props.currentIndex + 1}
                    </div>
                    <div
                      className={css(styles.pageSelectorBtn)}
                      style={{
                        // color: green[500],
                        color: '#5080B0',
                        fontSize: '18px',
                        borderTopRightRadius: '5px',
                        borderBottomRightRadius: '5px'
                      }}
                      onClick={() => { props.loadImage(Math.min(props.croppedImages.length - 1, props.currentIndex + 1)); }}
                    >
                      <i className='fa fa-plus-circle' />
                    </div>
                  </div>
                  <div className='pl-2' style={{ color: '#96C8FA' }}>
                    {`of ${props.croppedImages.length}`}
                  </div>
                </div>
                {/* Right aligned icons */}
                {props.editMode
                  ? <div className={css(styles.toolbarNav)}>
                    {/* Crop Icon */}
                    <div
                      className={`mr-4 ${css(styles.toolbarIcon)}`}
                      onClick={() => {
                        props.onModeChange(props.editMode ? 'view' : 'edit');
                      }}
                    >
                      <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M20 0v5.455h-1.818v9.09H20V20h-5.454v-1.818H5.455V20H0v-5.454h1.818V5.455H0V0h5.455v1.818h9.09V0H20ZM5.455 16.363h9.09v-1.818h1.819v-9.09h-1.818V3.635H5.455v1.818H3.637v9.091h1.818v1.818Z" fill="#00A0FF" /></svg>
                    </div>
                    <div className={`mr-4 ${css(styles.toolbarIcon)}`}>
                      <svg width="24" height="24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M11.417.038c.21-.02.413-.038.634-.038C18.34 0 23.49 4.84 24 11h-1.5c-.36-3.76-2.7-6.93-5.97-8.48L15.2 3.85 11.39.04l.026-.002ZM1.5 13c.36 3.76 2.7 6.93 5.97 8.49l1.33-1.34 3.81 3.82c-.073.003-.146.008-.218.012a7.058 7.058 0 0 1-.442.018C5.66 24 .51 19.16 0 13h1.5ZM16 14h2V8a2 2 0 0 0-2-2h-6v2h6v6ZM8 4v12h12v2h-2v2h-2v-2H8a2 2 0 0 1-2-2V8H4V6h2V4h2Z" fill="#96C8FF" /></svg>
                    </div>
                    {/* Delete Icon */}
                    <div
                      className={css(styles.toolbarIcon)}
                      onClick={() => {
                        setShowDeleteWarning(true);
                      }}
                    >
                      <svg width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M19.767 3.103a.839.839 0 0 0-.585-.237h-4.108V1.578c0-.87-.703-1.578-1.569-1.578H6.494c-.866 0-1.569.708-1.569 1.578v1.288H.797A.8.8 0 0 0 0 3.668c0 .443.356.802.797.802H1.5v13.952c0 .87.703 1.578 1.568 1.578h14.086c.866 0 1.569-.708 1.569-1.578V4.47h.458c.438 0 .819-.359.819-.802a.792.792 0 0 0-.233-.565ZM2.273 19.595a1.464 1.464 0 0 1 0 0Zm.215.123a1.075 1.075 0 0 0 .037.038l-.037-.038ZM13.5 1.584H6.492V2.88H13.5V1.584Zm3.655 16.835H3.07V4.47h14.083v13.95ZM9.188 7.962c0-.445.358-.806.8-.806.444 0 .802.361.802.806v7.48a.803.803 0 0 1-.801.805.803.803 0 0 1-.802-.806V7.962Zm-3.54-.806a.803.803 0 0 0-.8.806v7.48c0 .445.358.805.8.805.444 0 .802-.36.802-.806V7.962a.803.803 0 0 0-.801-.806Zm7.832.806c0-.445.359-.806.801-.806.444 0 .802.361.802.806v7.48a.803.803 0 0 1-.802.805.803.803 0 0 1-.8-.806V7.962Z" fill="#96C8FA" /></svg>
                    </div>
                  </div>
                  : <div className={css(styles.toolbarNav)}>
                    <div className={css(styles.toolbarIcon)}>
                      {/* Zoom Reset */}
                      <div
                        className={`mr-2 ${css(styles.miniBtn)}`}
                        style={{ display: enableZoom ? '' : 'none' }}
                        onClick={() => {
                          if (zoomRef.current) {
                            setIsZoomed(false);
                            zoomRef.current.style.height = '';
                          }
                        }}
                      >
                        <i
                          onClick={() => {
                            if (zoomRef.current) {
                              setIsZoomed(false);
                              zoomRef.current.style.height = '';
                            }
                          }}
                          className='fa fa-refresh'
                        />
                      </div>
                      {/* Zoom In */}
                      <div
                        className={`mr-2 ${css(styles.miniBtn)}`}
                        style={{ display: enableZoom ? '' : 'none' }}
                        onClick={() => {
                          if (zoomRef.current) {
                            setIsZoomed(true);
                            const lastHeight = zoomRef.current.clientHeight;
                            zoomRef.current.style.height = `${lastHeight + 20}px`;
                          }
                        }}
                      >
                        <i
                          onClick={() => {
                            if (zoomRef.current) {
                              setIsZoomed(true);
                              const lastHeight = zoomRef.current.clientHeight;
                              zoomRef.current.style.height = `${lastHeight + 20}px`;
                            }
                          }}
                          className='fa fa-search-plus'
                        />
                      </div>
                      {/* Zoom Out */}
                      <div
                        className={`mr-2 ${css(styles.miniBtn)}`}
                        style={{ display: enableZoom ? '' : 'none' }}
                        onClick={() => {
                          if (zoomRef.current) {
                            setIsZoomed(true);
                            const lastHeight = zoomRef.current.clientHeight;
                            zoomRef.current.style.height = `${lastHeight - 20}px`;
                          }
                        }}
                      >
                        <i
                          onClick={() => {
                            if (zoomRef.current) {
                              setIsZoomed(true);
                              const lastHeight = zoomRef.current.clientHeight;
                              zoomRef.current.style.height = `${lastHeight - 20}px`;
                            }
                          }}
                          className='fa fa-search-minus'
                        />
                      </div>
                      {/* Edit Icon */}
                      <div
                        className={`mr-2 ${css(styles.miniBtn)}`}
                        onClick={() => {
                          props.onModeChange(props.editMode ? 'view' : 'edit');
                        }}
                      >
                        <i onClick={props.onPrint} className='fa fa-pencil' />
                      </div>
                      {/* Print Icon */}
                      {/* <div
                        className={`mr-2 ${css(styles.miniBtn)}`}
                        onClick={props.onPrint}
                      >
                        <i onClick={props.onPrint} className='fa fa-print' />
                      </div> */}
                      {/* Fax Icon */}
                      {/* <div className={`mr-2 ${css(styles.disabledMiniBtn)}`}>
                        <i className='fa fa-fax' />
                      </div> */}
                      {/* Download Icon */}
                      <div onClick={props.onDownlaod} className={`mr-2 ${css(styles.miniBtn)}`}>
                        <i onClick={props.onDownlaod} style={{ fontSize: '16px' }} className='fa fa-download' />
                      </div>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
          <div
            ref={cropperParentRef}
            className={css(styles.cropperParent, props.editMode && styles.editModeParent)}
          >
            {props.editMode
              ? <Cropper
                src={props.croppedImages[props.currentIndex]}
                style={{
                  // height: 'calc(100% - 80px)'
                  height: `${cropperHeight}px`,
                  width: '100%'
                }}
                checkCrossOrigin
                checkOrientation
                background={false}
                viewMode={1}
                guides
                ref={ref}
                minCropBoxWidth={50}
                minCropBoxHeight={50}
                autoCropArea={1}
                dragMode='none'
                zoomable={false}
                cropend={props.onCrop}
              />
              : imageDisplay
            }
          </div>
        </div>
      </div>
      {/* Footer */}
      {props.showFooter &&
        <div className={css(styles.footer)}>
          <div className={css(styles.footerContainer)}>
            {/* ReScan & Revert Buttons */}
            <div
              style={{
                display: 'flex',
                flexFlow: 'column'
              }}
            >
              <Button
                btnType='primary'
                isTextBtn
                onClick={props.onReScan}
              >
                <i className='fa fa-camera mr-2' />
                ReScan
              </Button>
              <Button
                btnType='warning'
                isTextBtn
                isDisabled={props.images[props.currentIndex] === props.croppedImages[props.currentIndex]}
                onClick={() => { props.onRevert(props.currentIndex); }}
              >
                <i className='fa fa-undo mr-2' />
                Revert
              </Button>
            </div>
            {/* ReScan Button */}
            <div
              style={{
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'calc(100vw - 100px)'
              }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: '50%',
                  width: '60px',
                  height: '60px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <div
                  style={{
                    borderRadius: '50%',
                    border: '1px solid black',
                    background: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '55px',
                    height: '55px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    lineHeight: '12px',
                    fontWeight: 'bold'
                  }}
                  onClick={props.onScanNext}
                >
                  New<br />Page
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {/* New Scan */}
              {/* <Button className='ml-2' btnType='primary' onClick={props.onScanNext}>
                <i className='fa fa-plus-circle mr-1' />
                New
              </Button> */}
              {/* Accept / Finish -> depending upon state */}
              <Button
                className='ml-2'
                style={{ padding: '0', color: 'white', fontWeight: 'bold', width: '75px', height: '50px' }}
                btnType='primary'
                onClick={props.onFinish}
              >
                {'Save & Finish'}
              </Button>
            </div>
          </div>
        </div>
      }
    </div>
  );
});

CropperContainer.defaultProps = {
  title: 'Face Sheet',
  editMode: false,
  overrideDimensions: false,
  overrideWidth: 0,
  // overrideHeightFactor: 0,
  overrideHeight: 0,
  showHeader: true,
  showFooter: true,
  onModeChange: () => { }
};

export default CropperContainer;
